// WikiTree.jsx — recursive folder/page tree with expand/collapse, native
// drag reorder + move-into-folder, RBAC lock states, a per-folder AI agent entry,
// create (page/folder) menus, and per-node actions (rename / archive / delete).
//
// NOTE on DnD: nodes are rendered by a plain recursive render function (not a
// nested component) so re-renders reconcile in place instead of remounting —
// remounting mid-drag was cancelling the drag. The drag source is held in a
// ref (no setState on dragstart) and dataTransfer.setData is set (Firefox needs it).
import React, { useState, useEffect, useRef } from 'react';
import {
    ChevronRight, ChevronDown, Folder, FolderOpen, FileText, Lock, Plus, GripVertical, FileClock,
    MoreHorizontal, FilePlus, FolderPlus, Pencil, Archive, Trash2, Upload,
} from 'lucide-react';
import { DEPT_BY_ID, deptColors } from '../../lib/wikiConstants';
import { WikiAiAvatar } from './WikiCiteChip';

function countDocs(node) {
    if (node.kind === 'doc') return 1;
    return (node.children || []).reduce((a, c) => a + countDocs(c), 0);
}

function collectFolderIds(node, acc = new Set()) {
    if (node.kind === 'folder') {
        acc.add(node.folder_id);
        (node.children || []).forEach(c => collectFolderIds(c, acc));
    }
    return acc;
}

// New position so `fromId` lands just before `toId` within its siblings.
function reorderPosition(siblings, toId) {
    const idx = siblings.findIndex(s => (s.page_id || s.folder_id) === toId);
    if (idx < 0) return null;
    const targetPos = siblings[idx].position || 0;
    const prevPos = idx > 0 ? (siblings[idx - 1].position || 0) : targetPos - 1000;
    return (prevPos + targetPos) / 2;
}
function appendPosition(children) {
    const max = (children || []).reduce((m, c) => Math.max(m, c.position || 0), 0);
    return max + 1000;
}

// A small viewport-fixed dropdown (escapes the tree's overflow:auto clipping).
function PopMenu({ at, items, onClose }) {
    const W = 184;
    const left = Math.max(8, Math.min(at.x, window.innerWidth - W - 8));
    const top = at.y;
    return (
        <>
            <div className="fixed inset-0 z-40" onClick={onClose} onContextMenu={(e) => { e.preventDefault(); onClose(); }} />
            <div className="fixed z-50 bg-white rounded-xl border border-stone-200 wiki-shadow-float p-1" style={{ left, top, width: W }}>
                {items.map((it, i) => (
                    it.divider
                        ? <div key={i} className="my-1 border-t border-stone-100" />
                        : <button key={i} onClick={() => { onClose(); it.onClick(); }}
                            className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-left text-[13px] ${it.danger ? 'text-rose-600 hover:bg-rose-50' : 'text-stone-700 hover:bg-stone-50'}`}>
                            <it.icon className={`w-4 h-4 ${it.danger ? 'text-rose-500' : 'text-stone-500'}`} /> {it.label}
                        </button>
                ))}
            </div>
        </>
    );
}

export default function WikiTree({
    deptId, access, nodes, selectedId, onSelect, onOpenAgent,
    canEdit, onReorder, onCreate, onRename, onArchive, onDelete, onImport,
    onOpenArchived, archivedCount = 0,
}) {
    const dept = DEPT_BY_ID[deptId];
    const colors = deptColors(deptId);
    const [expanded, setExpanded] = useState({});
    const [overId, setOverId] = useState(null);
    const [menu, setMenu] = useState(null);        // { kind:'row'|'add', node?, parentId?, at:{x,y} }
    const dragRef = useRef(null);   // { id, parentId, kind, descIds:Set }
    const fileRef = useRef(null);   // hidden <input type=file> for import
    const importParentRef = useRef(null);
    const pickFile = (parentId) => { importParentRef.current = parentId; fileRef.current && fileRef.current.click(); };

    useEffect(() => {
        const e = {};
        (nodes || []).forEach(n => { if (n.kind === 'folder') e[n.folder_id] = true; });
        setExpanded(e);
    }, [deptId]); // eslint-disable-line react-hooks/exhaustive-deps

    const openMenuAt = (btn, payload) => {
        const r = btn.getBoundingClientRect();
        setMenu({ ...payload, at: { x: r.right - 184, y: r.bottom + 4 } });
    };

    const onDrop = (targetNode, targetParentId, targetSiblings) => {
        const d = dragRef.current;
        dragRef.current = null;
        setOverId(null);
        if (!d) return;
        const targetId = targetNode.folder_id || targetNode.page_id;
        if (d.id === targetId) return;
        // Guard: don't drop a folder into itself or its own subtree.
        if (d.kind === 'folder' && d.descIds && d.descIds.has(targetId)) return;

        if (targetNode.kind === 'folder' && d.parentId !== targetNode.folder_id) {
            // Move INTO this folder (append to its children).
            onReorder(d.kind, d.id, { newParentId: targetNode.folder_id, position: appendPosition(targetNode.children) });
        } else if (d.parentId === targetParentId) {
            // Reorder within the same parent.
            const pos = reorderPosition(targetSiblings, targetId);
            if (pos != null) onReorder(d.kind, d.id, { position: pos });
        } else {
            // Dropped on a node in a different parent → move there, near the target.
            const pos = reorderPosition(targetSiblings, targetId);
            onReorder(d.kind, d.id, { newParentId: targetParentId, position: pos != null ? pos : appendPosition(targetSiblings) });
        }
    };

    // Build the per-row "..." menu items for a node.
    const rowMenuItems = (node) => {
        const isFolder = node.kind === 'folder';
        const id = isFolder ? node.folder_id : node.page_id;
        if (isFolder) {
            return [
                { icon: FilePlus, label: 'New page', onClick: () => { setExpanded(s => ({ ...s, [id]: true })); onCreate('page', id); } },
                { icon: FolderPlus, label: 'New subfolder', onClick: () => { setExpanded(s => ({ ...s, [id]: true })); onCreate('folder', id); } },
                ...(onImport ? [{ icon: Upload, label: 'Upload file here…', onClick: () => { setExpanded(s => ({ ...s, [id]: true })); pickFile(id); } }] : []),
                { divider: true },
                { icon: Pencil, label: 'Rename', onClick: () => onRename('folder', id, node.name) },
                { icon: Archive, label: 'Archive', onClick: () => onArchive('folder', id, node.name) },
                { icon: Trash2, label: 'Delete', danger: true, onClick: () => onDelete('folder', id, node.name) },
            ];
        }
        return [
            { icon: Pencil, label: 'Rename', onClick: () => onRename('page', id, node.name) },
            { icon: Archive, label: 'Archive', onClick: () => onArchive('page', id, node.name) },
            { icon: Trash2, label: 'Delete', danger: true, onClick: () => onDelete('page', id, node.name) },
        ];
    };

    // Plain recursive render (NOT a component) so the DOM reconciles in place during drag.
    const renderNode = (node, depth, parentId, siblings) => {
        const isFolder = node.kind === 'folder';
        const id = isFolder ? node.folder_id : node.page_id;
        const open = expanded[id];
        const active = !isFolder && id === selectedId;
        const isOver = overId === id;
        const locked = !!node.locked;
        const draggable = canEdit && !locked;
        const showActions = canEdit && !locked;
        return (
            <div key={id}>
                <div
                    draggable={draggable}
                    onDragStart={draggable ? (e) => {
                        dragRef.current = { id, parentId, kind: isFolder ? 'folder' : 'page', descIds: isFolder ? collectFolderIds(node) : null };
                        e.dataTransfer.effectAllowed = 'move';
                        try { e.dataTransfer.setData('text/plain', id); } catch { /* noop */ }
                    } : undefined}
                    onDragEnd={() => { dragRef.current = null; setOverId(null); }}
                    onDragOver={(e) => { if (dragRef.current && dragRef.current.id !== id) { e.preventDefault(); if (overId !== id) setOverId(id); } }}
                    onDragLeave={() => { if (overId === id) setOverId(null); }}
                    onDrop={(e) => { e.preventDefault(); onDrop(node, parentId, siblings); }}
                    onClick={() => { if (locked) return; if (isFolder) setExpanded(s => ({ ...s, [id]: !s[id] })); else onSelect(id); }}
                    className={`group flex items-center gap-1.5 px-2 py-1.5 rounded-md text-[13px] cursor-pointer relative
                        ${active ? 'bg-stone-100 text-stone-800 font-semibold' : locked ? 'text-stone-400' : 'text-stone-600 hover:bg-stone-50'}
                        ${isOver ? 'ring-1 ring-stone-400 bg-stone-50' : ''}`}
                    style={{ paddingLeft: 8 + depth * 16 }}>
                    {draggable && (
                        <span className="opacity-0 group-hover:opacity-100 -ml-1 text-stone-300 cursor-grab" title="Drag to move/reorder"><GripVertical className="w-3 h-3" /></span>
                    )}
                    {isFolder
                        ? (open ? <ChevronDown className="w-3 h-3 text-stone-400 shrink-0" /> : <ChevronRight className="w-3 h-3 text-stone-400 shrink-0" />)
                        : <span className="w-3 shrink-0" />}
                    {isFolder
                        ? (open ? <FolderOpen className="w-[15px] h-[15px] text-amber-500 shrink-0" /> : <Folder className="w-[15px] h-[15px] text-amber-500 shrink-0" />)
                        : <FileText className={`w-[15px] h-[15px] shrink-0 ${active ? 'text-stone-900' : 'text-stone-500'}`} />}
                    <span className="flex-1 truncate">{node.name}</span>
                    {node.status === 'draft' && !isFolder && <FileClock className="w-3 h-3 text-amber-500 shrink-0" title="Draft" />}
                    {node.read_required && !isFolder && <span className="w-1.5 h-1.5 rounded-full bg-stone-600 shrink-0" title="Required reading" />}
                    {showActions && (
                        <button
                            onClick={(e) => { e.stopPropagation(); openMenuAt(e.currentTarget, { kind: 'row', node }); }}
                            title="Actions"
                            className="opacity-0 group-hover:opacity-100 text-stone-400 hover:text-stone-900 w-5 h-5 -my-1 rounded flex items-center justify-center hover:bg-stone-200/70 shrink-0">
                            <MoreHorizontal className="w-3.5 h-3.5" />
                        </button>
                    )}
                    {locked ? <Lock className="w-3 h-3 text-stone-400 shrink-0" />
                        : isFolder ? <span className="text-[10px] text-stone-400 font-mono shrink-0 group-hover:hidden">{countDocs(node)}</span> : null}
                </div>
                {isFolder && open && (
                    <div>{(node.children || []).map(c => renderNode(c, depth + 1, id, node.children))}</div>
                )}
            </div>
        );
    };

    const total = (nodes || []).reduce((a, n) => a + countDocs(n), 0);

    return (
        <div className="flex flex-col h-full">
            <div className="px-3 pt-3 pb-2 flex items-center gap-2">
                <div className={`w-6 h-6 rounded-md flex items-center justify-center ${colors.tile}`}>
                    {dept?.icon && <dept.icon className="w-3.5 h-3.5" />}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[12px] wiki-display font-bold text-stone-900 leading-tight">{dept?.name}</div>
                    <div className="text-[10px] text-stone-500 leading-tight">{total} documents · your access: <span className="font-semibold capitalize">{access}</span></div>
                </div>
            </div>

            <div className="px-2 pb-2">
                <button onClick={() => onOpenAgent(deptId)}
                    className="w-full wiki-ai-border rounded-lg bg-white p-2 flex items-center gap-2 text-left hover:bg-stone-100/40 transition">
                    <WikiAiAvatar size={28} />
                    <div className="min-w-0">
                        <div className="text-[12px] font-semibold wiki-grad-text leading-tight">{dept?.name} Agent</div>
                        <div className="text-[10px] text-stone-500 leading-tight truncate">Ask this folder anything</div>
                    </div>
                </button>
            </div>

            <div className="px-2 pb-1 flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider font-semibold text-stone-500 px-1">Pages</span>
                {canEdit && (
                    <button onClick={(e) => openMenuAt(e.currentTarget, { kind: 'add' })} title="Add page or folder"
                        className="text-stone-500 hover:text-stone-900 w-5 h-5 rounded flex items-center justify-center hover:bg-stone-100"><Plus className="w-3.5 h-3.5" /></button>
                )}
            </div>
            <div className="flex-1 overflow-auto px-2 pb-2 space-y-0.5">
                {(nodes || []).map(n => renderNode(n, 0, null, nodes))}
                {(!nodes || nodes.length === 0) && <div className="text-[12px] text-stone-400 px-2 py-3">No pages yet.</div>}
            </div>

            {canEdit && archivedCount > 0 && (
                <div className="px-2 pb-3 pt-1 border-t border-stone-100">
                    <button onClick={onOpenArchived}
                        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-[12px] text-stone-500 hover:bg-stone-50 hover:text-stone-700">
                        <Archive className="w-3.5 h-3.5 text-stone-400" />
                        <span className="flex-1 text-left">Archived</span>
                        <span className="text-[10px] font-mono text-stone-400">{archivedCount}</span>
                    </button>
                </div>
            )}

            {menu && (
                <PopMenu
                    at={menu.at}
                    onClose={() => setMenu(null)}
                    items={menu.kind === 'add'
                        ? [
                            { icon: FilePlus, label: 'New page', onClick: () => onCreate('page', null) },
                            { icon: FolderPlus, label: 'New folder', onClick: () => onCreate('folder', null) },
                            ...(onImport ? [{ icon: Upload, label: 'Upload .md / .docx…', onClick: () => pickFile(null) }] : []),
                        ]
                        : rowMenuItems(menu.node)}
                />
            )}

            <input ref={fileRef} type="file" accept=".md,.markdown,.mdown,.txt,.docx" className="hidden"
                onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f && onImport) onImport(f, importParentRef.current); }} />
        </div>
    );
}

export { countDocs };
